import React, { useState } from 'react';
import './TableHistory.css';
import Layout from '../../layout/Layout';
import { Card, Table } from 'react-bootstrap';
import { 
  Row, 
  Col, 
  Input, 
  Button, 
  Spin, 
  Alert, 
  DatePicker, 
  Select, 
  Space,
  Pagination,
  Typography,
  Statistic,
  message
} from 'antd';
import { 
  SearchOutlined, 
  TableOutlined, 
  StarOutlined, 
  CalendarOutlined, 
  FilePdfOutlined, 
  FileExcelOutlined,
  LoadingOutlined 
} from '@ant-design/icons';
import { FaTable, FaStar, FaWineGlass, FaCalendarAlt, FaGlassMartini } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import logo from '../../assets/logo.png';

const { Title, Text } = Typography;
const { Option } = Select;

const TableHistory = () => {
  const [formData, setFormData] = useState({
    tableNo: '',
    waiterNo: '',
    startDate: dayjs(),
    endDate: dayjs(),
    zone: 'Club'
  });

  const [loading, setLoading] = useState(false);
  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const exportToExcel = () => {
    if (!reportData) {
      message.warning('No data to export');
      return;
    }

    let worksheetData = [];
    let fileName = `${activeReport}_${dayjs().format('YYYY-MM-DD')}.xlsx`;

    if (activeReport === 'Waiter Analysis') {
      worksheetData = reportData.bar_orders.concat(reportData.food_orders).map(order => ({
        Date: new Date(order.setup_dte).toLocaleDateString(),
        'KOT No': order.kot_no,
        'Table No': order.table_no,
        'Waiter No': reportData.waiter_no,
        'Item Name': order.item_name,
        Category: order.category || 'N/A',
        Quantity: order.quantity,
        Rate: order.rate,
        Amount: order.amount
      }));
    } else if (activeReport === 'Table Summary') {
      worksheetData = reportData.table_summary.map(table => ({
        'Table No': table.tables,
        'Bar Amount': table.bar_amount,
        'Food Amount': table.food_amount,
        'Shisha Amount': table.shisha_amount,
        'Discount': table.discount || 0,
        'Total Amount': table.total_amount || 0
      }));
    } else if (activeReport === 'VIP Table Summary') {
      worksheetData = reportData.vip_tables.map(table => ({
        'Table No': table.table_no,
        'Bar Orders': table.bar_orders,
        'Bar Amount': table.bar_amount,
        'Food Orders': table.food_orders,
        'Food Amount': table.food_amount,
        'Total Orders': table.total_orders,
        'Total Amount': table.total_amount
      }));
    } else if (activeReport === 'Shisha Analysis') {
      worksheetData = reportData.items.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Orders': item.order_count,
        'Quantity': item.total_quantity,
        'Total Amount': item.total_amount,
        'Avg Rate': item.average_rate,
        'Tables': item.unique_tables,
        'Days Sold': item.days_sold
      }));
    } else if (activeReport === 'Kayotee Analysis') {
      worksheetData = reportData.kayotee_breakdown.map(item => ({
        'Kayotee': item.kayote,
        'Total Drinks': item.total_drinks
      }));
    } else if (activeReport === 'Shisha Analysis by Date') {
      worksheetData = reportData.daily_breakdown.map(day => ({
        'Date': new Date(day.date).toLocaleDateString(),
        'Orders': day.order_count,
        'Quantity': day.total_quantity,
        'Total Amount': day.total_amount,
        'Unique Items': day.unique_items,
        'Unique Tables': day.unique_tables,
        'Unique Waiters': day.unique_waiters
      }));
    } else if (activeReport.includes('Entertainment')) {
      worksheetData = reportData.bills.map(bill => ({
        Date: new Date(bill.setup_dte).toLocaleDateString(),
        'KOT No': bill.kot_no,
        'Table No': bill.table_no,
        'Waiter No': bill.waiter_no,
        'Item Name': bill.item_name,
        Category: bill.category || 'N/A',
        Quantity: bill.quantity,
        Rate: bill.rate,
        Amount: bill.amount
      }));
    }

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, activeReport);
    XLSX.writeFile(workbook, fileName);
    message.success('Excel file downloaded successfully!');
  };

  const exportToPDF = () => {
    if (!reportData) {
      toast.warning('No data to export');
      return;
    }

    // Use portrait mode for all reports
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Soft color palette
    const headerColor = [147, 197, 253]; // Soft blue
    const summaryColor = [254, 240, 138]; // Soft yellow
    const alternateRowColor = [243, 244, 246]; // Light gray

    // Add logo to header
    const logoWidth = 30;
    const logoHeight = 15;
    const logoX = 10;
    const logoY = 5;
    doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

    // Title
    doc.setFontSize(18);
    doc.setTextColor(59, 130, 246);
    doc.text(activeReport, pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Date Range: ${formData.startDate.format('YYYY-MM-DD')} to ${formData.endDate.format('YYYY-MM-DD')}`, pageWidth / 2, 22, { align: 'center' });

    let startY = 30;

    if (activeReport === 'Waiter Analysis') {
      // Summary section
      doc.setFillColor(...summaryColor);
      doc.rect(10, startY, pageWidth - 20, 25, 'F');
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text(`Waiter: ${reportData.waiter_no}`, 15, startY + 8);
      doc.text(`Bar Orders: ${reportData.summary.bar.total_orders}`, 15, startY + 15);
      doc.text(`Food Orders: ${reportData.summary.food.total_orders}`, 15, startY + 22);
      doc.text(`Total Amount: ${formatCurrency(reportData.total_combined_amount)}`, pageWidth - 15, startY + 15, { align: 'right' });

      startY += 30;

      // Bar Orders
      if (reportData.bar_orders.length > 0) {
        doc.autoTable({
          startY: startY,
          head: [['Date', 'KOT No', 'Table No', 'Item Name', 'Category', 'Qty', 'Rate', 'Amount']],
          body: reportData.bar_orders.map(order => [
            new Date(order.setup_dte).toLocaleDateString(),
            order.kot_no,
            order.table_no,
            order.item_name,
            order.category,
            order.quantity,
            order.rate,
            order.amount
          ]),
          headStyles: { fillColor: headerColor, textColor: [0, 0, 0], fontSize: 9 },
          alternateRowStyles: { fillColor: alternateRowColor },
          styles: { fontSize: 8, cellPadding: 2 },
          margin: { left: 10, right: 10 }
        });
        startY = doc.lastAutoTable.finalY + 10;
      }

      // Food Orders
      if (reportData.food_orders.length > 0) {
        doc.autoTable({
          startY: startY,
          head: [['Date', 'KOT No', 'Table No', 'Item Name', 'Qty', 'Rate', 'Amount']],
          body: reportData.food_orders.map(order => [
            new Date(order.setup_dte).toLocaleDateString(),
            order.kot_no,
            order.table_no,
            order.item_name,
            order.quantity,
            order.rate,
            order.amount
          ]),
          headStyles: { fillColor: headerColor, textColor: [0, 0, 0], fontSize: 9 },
          alternateRowStyles: { fillColor: alternateRowColor },
          styles: { fontSize: 8, cellPadding: 2 },
          margin: { left: 10, right: 10 }
        });
      }
    } else if (activeReport === 'Table Summary' || activeReport === 'VIP Table Summary') {
      const data = activeReport === 'Table Summary' ? reportData.table_summary : reportData.vip_tables;
      const total = activeReport === 'Table Summary' ? reportData.grand_total : reportData.total_vip_sales;
      const isVIP = activeReport === 'VIP Table Summary';

      doc.autoTable({
        startY: startY,
        head: isVIP ? [['Table No', 'Bar Orders', 'Bar Amount', 'Food Orders', 'Food Amount', 'Total Orders', 'Total Amount']] : [['Table No', 'Bar Amount', 'Food Amount', 'Shisha Amount', 'Discount', 'Total Amount']],
        body: data.map(table => isVIP ? [
          table.table_no,
          table.bar_orders,
          table.bar_amount,
          table.food_orders,
          table.food_amount,
          table.total_orders,
          table.total_amount || 0
        ] : [
          table.tables,
          table.bar_amount,
          table.food_amount,
          table.shisha_amount || 0,
          table.discount || 0,
          table.total_amount || 0
        ]),
        headStyles: { fillColor: headerColor, textColor: [0, 0, 0], fontSize: 9 },
        alternateRowStyles: { fillColor: alternateRowColor },
        styles: { fontSize: 8, cellPadding: 2 },
        margin: { left: 10, right: 10 }
      });

      // Summary at bottom
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFillColor(...summaryColor);
      doc.rect(10, finalY, pageWidth - 20, 15, 'F');
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text(`Total Tables: ${data.length}`, 15, finalY + 7);
      doc.text(`Grand Total: ${Number(total || 0).toFixed(2)}`, pageWidth - 15, finalY + 7, { align: 'right' });
    } else if (activeReport === 'Shisha Analysis') {
      doc.autoTable({
        startY: startY,
        head: [['Item Name', 'Category', 'Orders', 'Quantity', 'Total Amount', 'Avg Rate', 'Tables', 'Days Sold']],
        body: reportData.items.map(item => [
          item.item_name,
          item.category,
          item.order_count,
          item.total_quantity,
          item.total_amount,
          item.average_rate,
          item.unique_tables,
          item.days_sold
        ]),
        headStyles: { fillColor: headerColor, textColor: [0, 0, 0], fontSize: 9 },
        alternateRowStyles: { fillColor: alternateRowColor },
        styles: { fontSize: 8, cellPadding: 2 },
        margin: { left: 10, right: 10 }
      });

      // Summary at bottom
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFillColor(...summaryColor);
      doc.rect(10, finalY, pageWidth - 20, 15, 'F');
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text(`Total Items: ${reportData.summary.total_items}`, 15, finalY + 7);
      doc.text(`Total Amount: ${Number(reportData.summary.total_amount || 0).toFixed(2)}`, pageWidth - 15, finalY + 7, { align: 'right' });
    } else if (activeReport === 'Shisha Analysis by Date') {
      doc.autoTable({
        startY: startY,
        head: [['Date', 'Orders', 'Quantity', 'Total Amount', 'Unique Items', 'Unique Tables', 'Unique Waiters']],
        body: reportData.daily_breakdown.map(day => [
          new Date(day.date).toLocaleDateString(),
          day.order_count,
          day.total_quantity,
          day.total_amount,
          day.unique_items,
          day.unique_tables,
          day.unique_waiters
        ]),
        headStyles: { fillColor: headerColor, textColor: [0, 0, 0], fontSize: 9 },
        alternateRowStyles: { fillColor: alternateRowColor },
        styles: { fontSize: 8, cellPadding: 2 },
        margin: { left: 10, right: 10 }
      });

      // Summary at bottom
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFillColor(...summaryColor);
      doc.rect(10, finalY, pageWidth - 20, 15, 'F');
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text(`Total Days: ${reportData.summary.total_days}`, 15, finalY + 7);
      doc.text(`Total Amount: ${Number(reportData.summary.total_amount || 0).toFixed(2)}`, pageWidth - 15, finalY + 7, { align: 'right' });
    } else if (activeReport.includes('Entertainment')) {
      doc.autoTable({
        startY: startY,
        head: [['Date', 'KOT No', 'Table No', 'Waiter No', 'Item Name', 'Category', 'Qty', 'Rate', 'Amount']],
        body: reportData.bills.map(bill => [
          new Date(bill.setup_dte).toLocaleDateString(),
          bill.kot_no,
          bill.table_no,
          bill.waiter_no,
          bill.item_name,
          bill.category || 'N/A',
          bill.quantity,
          bill.rate,
          bill.amount
        ]),
        headStyles: { fillColor: headerColor, textColor: [0, 0, 0], fontSize: 9 },
        alternateRowStyles: { fillColor: alternateRowColor },
        styles: { fontSize: 8, cellPadding: 2 },
        margin: { left: 10, right: 10 }
      });

      // Summary at bottom
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFillColor(...summaryColor);
      doc.rect(10, finalY, pageWidth - 20, 15, 'F');
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text(`Total Bills: ${reportData.summary.total_bills}`, 15, finalY + 7);
      doc.text(`Total Amount: ${Number(reportData.summary.total_amount || 0).toFixed(2)}`, pageWidth - 15, finalY + 7, { align: 'right' });
    }

    doc.save(`${activeReport}_${dayjs().format('YYYY-MM-DD')}.pdf`);
    message.success('PDF downloaded successfully!');
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const renderPagination = (totalItems) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;

    return (
      <div className="pagination-controls" style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Text>Show</Text>
          <Select
            size="small"
            value={itemsPerPage}
            onChange={(value) => {
              setItemsPerPage(value);
              setCurrentPage(1);
            }}
            style={{ width: 80 }}
          >
            <Option value={5}>5</Option>
            <Option value={10}>10</Option>
            <Option value={25}>25</Option>
            <Option value={50}>50</Option>
            <Option value={100}>100</Option>
          </Select>
          <Text>entries | Page {currentPage} of {totalPages}</Text>
        </Space>
        <Pagination
          size="small"
          current={currentPage}
          total={totalItems}
          pageSize={itemsPerPage}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
        />
      </div>
    );
  };

  const handleSearchByWaiter = async () => {
    if (!formData.waiterNo) {
      message.warning('Please enter Waiter Number');
      return;
    }

    try {
      setLoading(true);
      setActiveReport('Waiter Analysis');
      setCurrentPage(1);

      const queryParams = new URLSearchParams({
        waiter_no: formData.waiterNo,
        start_date: formData.startDate.format('YYYY-MM-DD'),
        end_date: formData.endDate.format('YYYY-MM-DD')
      }).toString();

      const response = await axios.get(
        `/table-history/search-by-waiter?${queryParams}`,
        getHeaders()
      );

      if (response.data.success) {
        setReportData(response.data.data);
        message.success('Waiter Analysis report loaded successfully!');
      } else {
        message.error('Failed to load report');
        setReportData(null);
      }
    } catch (error) {
      console.error('Error fetching Waiter Analysis:', error);
      message.error(error.response?.data?.error || 'Failed to fetch Waiter Analysis');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTableSummary = async () => {
    try {
      setLoading(true);
      setActiveReport('Table Summary');

      const params = {
        start_date: formData.startDate.format('YYYY-MM-DD'),
        end_date: formData.endDate.format('YYYY-MM-DD')
      };

      if (formData.tableNo) {
        params.table_no = formData.tableNo;
      }

      const queryParams = new URLSearchParams(params).toString();

      const response = await axios.get(
        `/table-history/table-summary?${queryParams}`,
        getHeaders()
      );

      if (response.data.success) {
        setReportData(response.data.data);
        message.success('Table Summary report loaded successfully!');
      } else {
        message.error('Failed to load report');
        setReportData(null);
      }
    } catch (error) {
      console.error('Error fetching Table Summary:', error);
      message.error(error.response?.data?.error || 'Failed to fetch Table Summary');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleVIPTableSummary = async () => {
    try {
      setLoading(true);
      setActiveReport('VIP Table Summary');

      const queryParams = new URLSearchParams({
        start_date: formData.startDate.format('YYYY-MM-DD'),
        end_date: formData.endDate.format('YYYY-MM-DD')
      }).toString();

      const response = await axios.get(
        `/table-history/vip-table-summary?${queryParams}`,
        getHeaders()
      );

      if (response.data.success) {
        setReportData(response.data.data);
        message.success('VIP Table Summary report loaded successfully!');
      } else {
        message.error('Failed to load report');
        setReportData(null);
      }
    } catch (error) {
      console.error('Error fetching VIP Table Summary:', error);
      message.error(error.response?.data?.error || 'Failed to fetch VIP Table Summary');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleShishaAnalysis = async () => {
    try {
      setLoading(true);
      setActiveReport('Shisha Analysis');

      const queryParams = new URLSearchParams({
        start_date: formData.startDate.format('YYYY-MM-DD'),
        end_date: formData.endDate.format('YYYY-MM-DD')
      }).toString();

      const response = await axios.get(
        `/table-history/shisha-analysis?${queryParams}`,
        getHeaders()
      );

      if (response.data.success) {
        setReportData(response.data.data);
        message.success('Shisha Analysis report loaded successfully!');
      } else {
        message.error('Failed to load report');
        setReportData(null);
      }
    } catch (error) {
      console.error('Error fetching Shisha Analysis:', error);
      message.error(error.response?.data?.error || 'Failed to fetch Shisha Analysis');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleShishaByDate = async () => {
    try {
      setLoading(true);
      setActiveReport('Shisha Analysis by Date');

      const queryParams = new URLSearchParams({
        start_date: formData.startDate.format('YYYY-MM-DD'),
        end_date: formData.endDate.format('YYYY-MM-DD')
      }).toString();

      const response = await axios.get(
        `/table-history/shisha-analysis-by-date?${queryParams}`,
        getHeaders()
      );

      if (response.data.success) {
        setReportData(response.data.data);
        message.success('Shisha Analysis by Date report loaded successfully!');
      } else {
        message.error('Failed to load report');
        setReportData(null);
      }
    } catch (error) {
      console.error('Error fetching Shisha Analysis by Date:', error);
      message.error(error.response?.data?.error || 'Failed to fetch Shisha Analysis by Date');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBarEntertainment = async () => {
    try {
      setLoading(true);
      setActiveReport('Bar Entertainment Bills');

      const queryParams = new URLSearchParams({
        start_date: formData.startDate.format('YYYY-MM-DD'),
        end_date: formData.endDate.format('YYYY-MM-DD')
      }).toString();

      const response = await axios.get(
        `/table-history/bar-entertainment?${queryParams}`,
        getHeaders()
      );

      if (response.data.success) {
        setReportData(response.data.data);
        message.success('Bar Entertainment Bills report loaded successfully!');
      } else {
        message.error('Failed to load report');
        setReportData(null);
      }
    } catch (error) {
      console.error('Error fetching Bar Entertainment Bills:', error);
      message.error(error.response?.data?.error || 'Failed to fetch Bar Entertainment Bills');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFoodEntertainment = async () => {
    try {
      setLoading(true);
      setActiveReport('Food Entertainment Bills');

      const queryParams = new URLSearchParams({
        start_date: formData.startDate.format('YYYY-MM-DD'),
        end_date: formData.endDate.format('YYYY-MM-DD')
      }).toString();

      const response = await axios.get(
        `/table-history/food-entertainment?${queryParams}`,
        getHeaders()
      );

      if (response.data.success) {
        setReportData(response.data.data);
        message.success('Food Entertainment Bills report loaded successfully!');
      } else {
        message.error('Failed to load report');
        setReportData(null);
      }
    } catch (error) {
      console.error('Error fetching Food Entertainment Bills:', error);
      message.error(error.response?.data?.error || 'Failed to fetch Food Entertainment Bills');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKayoteeAnalysis = async () => {
    try {
      setLoading(true);
      setActiveReport('Kayotee Analysis');

      const queryParams = new URLSearchParams({
        start_date: formData.startDate.format('YYYY-MM-DD'),
        end_date: formData.endDate.format('YYYY-MM-DD')
      }).toString();

      const response = await axios.get(
        `/table-history/kayotee-analysis?${queryParams}`,
        getHeaders()
      );

      if (response.data.success) {
        setReportData(response.data.data);
        message.success('Kayotee Analysis report loaded successfully!');
      } else {
        message.error('Failed to load report');
        setReportData(null);
      }
    } catch (error) {
      console.error('Error fetching Kayotee Analysis:', error);
      message.error(error.response?.data?.error || 'Failed to fetch Kayotee Analysis');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const renderWaiterAnalysis = () => {
    if (!reportData) return null;

    const barColumns = [
      { title: 'Date', dataIndex: 'setup_dte', key: 'date', render: (text) => dayjs(text).format('DD/MM/YYYY') },
      { title: 'Table No', dataIndex: 'tables', key: 'tables' },
      { title: 'Item Name', dataIndex: 'item_name', key: 'item_name' },
      { title: 'Category', dataIndex: 'category', key: 'category' },
      { title: 'Qty', dataIndex: 'quantity', key: 'quantity' },
      { title: 'Rate', dataIndex: 'rate', key: 'rate', render: (text) => formatCurrency(text) },
      { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (text) => formatCurrency(text) },
    ];

    const foodColumns = [
      { title: 'Date', dataIndex: 'setup_dte', key: 'date', render: (text) => dayjs(text).format('DD/MM/YYYY') },
      { title: 'Table No', dataIndex: 'tables', key: 'tables' },
      { title: 'Item Name', dataIndex: 'item_name', key: 'item_name' },
      { title: 'Qty', dataIndex: 'quantity', key: 'quantity' },
      { title: 'Rate', dataIndex: 'rate', key: 'rate', render: (text) => formatCurrency(text) },
      { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (text) => formatCurrency(text) },
    ];

    return (
      <div className="th-report-results">
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={8}>
            <Card className="th-summary-card bar-summary" bordered={false}>
              <Statistic
                title={<span><span style={{ fontSize: '24px' }}>🍸</span> Bar Orders</span>}
                value={reportData.summary.bar.total_orders}
                suffix={<div><div>Qty: {reportData.summary.bar.total_quantity}</div><div>{formatCurrency(reportData.summary.bar.total_amount)}</div></div>}
              />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card className="th-summary-card food-summary" bordered={false}>
              <Statistic
                title={<span><span style={{ fontSize: '24px' }}>🍕</span> Food Orders</span>}
                value={reportData.summary.food.total_orders}
                suffix={<div><div>Qty: {reportData.summary.food.total_quantity}</div><div>{formatCurrency(reportData.summary.food.total_amount)}</div></div>}
              />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card className="th-summary-card total-summary" bordered={false}>
              <Statistic
                title={<span><span style={{ fontSize: '24px' }}>💰</span> Total Combined</span>}
                value={formatCurrency(reportData.total_combined_amount)}
                prefix={`Waiter: ${reportData.waiter_no} |`}
              />
            </Card>
          </Col>
        </Row>

        {reportData.bar_orders && reportData.bar_orders.length > 0 && (
          <Card title={<span>🍸 Bar Orders Details ({reportData.bar_orders.length} items)</span>} className="mb-4">
            <Table
              columns={barColumns}
              dataSource={reportData.bar_orders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)}
              rowKey={(record, index) => index}
              pagination={false}
              size="small"
              bordered
              summary={() => (
                <Table.Summary.Row style={{ backgroundColor: '#ffebee', fontWeight: 'bold' }}>
                  <Table.Summary.Cell index={0} colSpan={3}><strong>TOTAL</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}><strong>{reportData.bar_orders.reduce((sum, order) => sum + (Number(order.quantity) || 0), 0)}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={4}></Table.Summary.Cell>
                  <Table.Summary.Cell index={5}><strong>{formatCurrency(reportData.bar_orders.reduce((sum, order) => sum + (Number(order.amount) || 0), 0))}</strong></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
            {renderPagination(reportData.bar_orders.length)}
          </Card>
        )}

        {reportData.food_orders && reportData.food_orders.length > 0 && (
          <Card title={<span>🍕 Food Orders Details ({reportData.food_orders.length} items)</span>}>
            <Table
              columns={foodColumns}
              dataSource={reportData.food_orders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)}
              rowKey={(record, index) => index}
              pagination={false}
              size="small"
              bordered
              summary={() => (
                <Table.Summary.Row style={{ backgroundColor: '#e8f5e9', fontWeight: 'bold' }}>
                  <Table.Summary.Cell index={0} colSpan={2}><strong>TOTAL</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={2}><strong>{reportData.food_orders.reduce((sum, order) => sum + (Number(order.quantity) || 0), 0)}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}></Table.Summary.Cell>
                  <Table.Summary.Cell index={4}><strong>{formatCurrency(reportData.food_orders.reduce((sum, order) => sum + (Number(order.amount) || 0), 0))}</strong></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
            {renderPagination(reportData.food_orders.length)}
          </Card>
        )}
      </div>
    );
  };

  const renderTableSummary = () => {
    if (!reportData || !reportData.table_summary) return null;

    return (
      <div className="th-report-results">
        <Card className="th-summary-card-header mb-4">
          <Card.Body>
            <Row>
              <Col md={4}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">📊</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Tables</div>
                    <div className="th-stat-value">{reportData.total_tables}</div>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">💰</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Grand Total</div>
                    <div className="th-stat-value">{formatCurrency(reportData.grand_total)}</div>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">📈</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Average/Table</div>
                    <div className="th-stat-value">
                      {formatCurrency(reportData.grand_total / (reportData.total_tables || 1))}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="th-data-table-card">
          <Card.Header className="th-table-header">
            <h5><FaTable className="me-2" />Table-wise Sales Summary ({reportData.table_summary.length} tables)</h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Table No</th>
                    <th>Bar Amount</th>
                    <th>Food Amount</th>
                    <th>Shisha Amount</th>
                    <th>Discount</th>
                    <th>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Sort by table number in ascending order
                    const sortedData = [...reportData.table_summary].sort((a, b) => {
                      const tableA = parseInt(a.tables) || 0;
                      const tableB = parseInt(b.tables) || 0;
                      return tableA - tableB;
                    });
                    const indexOfLastItem = currentPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
                    return currentItems.map((table, idx) => (
                      <tr key={idx}>
                        <td><strong>{table.tables}</strong></td>
                        <td>{formatCurrency(table.bar_amount)}</td>
                        <td>{formatCurrency(table.food_amount)}</td>
                        <td>{formatCurrency(table.shisha_amount || 0)}</td>
                        <td className="text-danger">{formatCurrency(table.discount || 0)}</td>
                        <td className="th-total-amount"><strong>{formatCurrency(table.total_amount || 0)}</strong></td>
                      </tr>
                    ));
                  })()}
                </tbody>
                <tfoot style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                  <tr>
                    <td><strong>TOTAL</strong></td>
                    <td><strong>{formatCurrency(reportData.table_summary.reduce((sum, t) => sum + (Number(t.bar_amount) || 0), 0))}</strong></td>
                    <td><strong>{formatCurrency(reportData.table_summary.reduce((sum, t) => sum + (Number(t.food_amount) || 0), 0))}</strong></td>
                    <td><strong>{formatCurrency(reportData.table_summary.reduce((sum, t) => sum + (Number(t.shisha_amount) || 0), 0))}</strong></td>
                    <td className="text-danger"><strong>{formatCurrency(reportData.table_summary.reduce((sum, t) => sum + (Number(t.discount) || 0), 0))}</strong></td>
                    <td className="th-total-amount"><strong>{formatCurrency(reportData.grand_total)}</strong></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
            {renderPagination(reportData.table_summary.length)}
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderVIPTableSummary = () => {
    if (!reportData || !reportData.vip_tables) return null;

    return (
      <div className="th-report-results">
        <Card className="th-summary-card-header th-vip-header mb-4">
          <Card.Body>
            <Row>
              <Col md={4}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">⭐</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">VIP Tables</div>
                    <div className="th-stat-value">{reportData.total_vip_tables}</div>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">💎</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total VIP Sales</div>
                    <div className="th-stat-value">{formatCurrency(reportData.total_vip_sales)}</div>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">📊</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Average/VIP Table</div>
                    <div className="th-stat-value">
                      {formatCurrency(reportData.total_vip_sales / (reportData.total_vip_tables || 1))}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="th-data-table-card">
          <Card.Header className="th-vip-table-header">
            <h5><FaStar className="me-2" />VIP Tables (201-223) Sales Summary</h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Table No</th>
                    <th>Bar Orders</th>
                    <th>Bar Amount</th>
                    <th>Food Orders</th>
                    <th>Food Amount</th>
                    <th>Total Orders</th>
                    <th>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const indexOfLastItem = currentPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = reportData.vip_tables.slice(indexOfFirstItem, indexOfLastItem);
                    return currentItems.map((table, idx) => (
                      <tr key={idx} className="vip-row">
                        <td><strong className="th-vip-table-no">{table.table_no}</strong></td>
                        <td>{table.bar_orders}</td>
                        <td>{formatCurrency(table.bar_amount)}</td>
                        <td>{table.food_orders}</td>
                        <td>{formatCurrency(table.food_amount)}</td>
                        <td><strong>{table.total_orders}</strong></td>
                        <td className="th-total-amount"><strong>{formatCurrency(table.total_amount)}</strong></td>
                      </tr>
                    ));
                  })()}
                </tbody>
                <tfoot style={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>
                  <tr>
                    <td><strong>TOTAL</strong></td>
                    <td><strong>{reportData.vip_tables.reduce((sum, t) => sum + (Number(t.bar_orders) || 0), 0)}</strong></td>
                    <td><strong>{formatCurrency(reportData.vip_tables.reduce((sum, t) => sum + (Number(t.bar_amount) || 0), 0))}</strong></td>
                    <td><strong>{reportData.vip_tables.reduce((sum, t) => sum + (Number(t.food_orders) || 0), 0)}</strong></td>
                    <td><strong>{formatCurrency(reportData.vip_tables.reduce((sum, t) => sum + (Number(t.food_amount) || 0), 0))}</strong></td>
                    <td><strong>{reportData.vip_tables.reduce((sum, t) => sum + (Number(t.total_orders) || 0), 0)}</strong></td>
                    <td className="th-total-amount"><strong>{formatCurrency(reportData.total_vip_sales)}</strong></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
            {renderPagination(reportData.vip_tables.length)}
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderShishaAnalysis = () => {
    if (!reportData || !reportData.items) return null;

    return (
      <div className="th-report-results">
        <Card className="th-summary-card-header th-shisha-header mb-4">
          <Card.Body>
            <Row>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">💨</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Items</div>
                    <div className="th-stat-value">{reportData.summary.total_items}</div>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">📦</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Quantity</div>
                    <div className="th-stat-value">{reportData.summary.total_quantity}</div>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">🛒</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Orders</div>
                    <div className="th-stat-value">{reportData.summary.total_orders}</div>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">💰</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Amount</div>
                    <div className="th-stat-value">{formatCurrency(reportData.summary.total_amount)}</div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="th-data-table-card">
          <Card.Header className="th-shisha-table-header">
            <h5><FaWineGlass className="me-2" />Shisha Items Analysis</h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Item Name</th>
                    <th>Category</th>
                    <th>Orders</th>
                    <th>Quantity</th>
                    <th>Total Amount</th>
                    <th>Avg Rate</th>
                    <th>Tables</th>
                    <th>Days Sold</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const indexOfLastItem = currentPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = reportData.items.slice(indexOfFirstItem, indexOfLastItem);
                    return currentItems.map((item, idx) => (
                      <tr key={idx}>
                        <td><strong>{item.item_name}</strong></td>
                        <td>{item.category}</td>
                        <td>{item.order_count}</td>
                        <td>{item.total_quantity}</td>
                        <td className="th-amount-cell">{formatCurrency(item.total_amount)}</td>
                        <td>{formatCurrency(item.average_rate)}</td>
                        <td>{item.unique_tables}</td>
                        <td>{item.days_sold}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
                <tfoot style={{ backgroundColor: '#e8f5e9', fontWeight: 'bold' }}>
                  <tr>
                    <td colSpan="2"><strong>TOTAL</strong></td>
                    <td><strong>{reportData.items.reduce((sum, item) => sum + (Number(item.order_count) || 0), 0)}</strong></td>
                    <td><strong>{reportData.items.reduce((sum, item) => sum + (Number(item.total_quantity) || 0), 0)}</strong></td>
                    <td className="th-amount-cell"><strong>{formatCurrency(reportData.summary.total_amount)}</strong></td>
                    <td colSpan="3"></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
            {renderPagination(reportData.items.length)}
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderShishaByDate = () => {
    if (!reportData || !reportData.daily_breakdown) return null;

    return (
      <div className="th-report-results">
        <Card className="th-summary-card-header th-shisha-date-header mb-4">
          <Card.Body>
            <Row>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">📅</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Days</div>
                    <div className="th-stat-value">{reportData.summary.total_days}</div>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">📦</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Quantity</div>
                    <div className="th-stat-value">{reportData.summary.total_quantity}</div>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">💰</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Amount</div>
                    <div className="th-stat-value">{formatCurrency(reportData.summary.total_amount)}</div>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">📊</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Avg Daily Sales</div>
                    <div className="th-stat-value">{formatCurrency(reportData.summary.average_daily_sales)}</div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="th-data-table-card">
          <Card.Header className="th-date-header">
            <h5><FaCalendarAlt className="me-2" />Daily Shisha Sales Breakdown</h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Orders</th>
                    <th>Quantity</th>
                    <th>Total Amount</th>
                    <th>Unique Items</th>
                    <th>Unique Tables</th>
                    <th>Unique Waiters</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const indexOfLastItem = currentPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = reportData.daily_breakdown.slice(indexOfFirstItem, indexOfLastItem);
                    return currentItems.map((day, idx) => (
                      <tr key={idx}>
                        <td><strong>{new Date(day.date).toLocaleDateString()}</strong></td>
                        <td>{day.order_count}</td>
                        <td>{day.total_quantity}</td>
                        <td className="th-amount-cell">{formatCurrency(day.total_amount)}</td>
                        <td>{day.unique_items}</td>
                        <td>{day.unique_tables}</td>
                        <td>{day.unique_waiters}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
                <tfoot style={{ backgroundColor: '#fce4ec', fontWeight: 'bold' }}>
                  <tr>
                    <td><strong>TOTAL</strong></td>
                    <td><strong>{reportData.daily_breakdown.reduce((sum, day) => sum + (Number(day.order_count) || 0), 0)}</strong></td>
                    <td><strong>{reportData.daily_breakdown.reduce((sum, day) => sum + (Number(day.total_quantity) || 0), 0)}</strong></td>
                    <td className="th-amount-cell"><strong>{formatCurrency(reportData.summary.total_amount)}</strong></td>
                    <td colSpan="3"></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
            {renderPagination(reportData.daily_breakdown.length)}
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderKayoteeAnalysis = () => {
    if (!reportData || !reportData.kayotee_breakdown) return null;

    return (
      <div className="th-report-results">
        <Card className="th-summary-card-header mb-4" style={{ background: 'linear-gradient(135deg, #fff9c4 0%, #fff59d 100%)' }}>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">🍹</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Kayotees</div>
                    <div className="th-stat-value">{reportData.summary.total_kayotees}</div>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">🥤</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Drinks</div>
                    <div className="th-stat-value">{reportData.summary.total_drinks}</div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="th-data-table-card">
          <Card.Header style={{ background: 'linear-gradient(90deg, #fff59d 0%, #ffee58 100%)' }}>
            <h5><FaGlassMartini className="me-2" />Kayotee Breakdown</h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Kayotee</th>
                    <th>Total Drinks</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const indexOfLastItem = currentPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = reportData.kayotee_breakdown.slice(indexOfFirstItem, indexOfLastItem);
                    return currentItems.map((item, idx) => (
                      <tr key={idx}>
                        <td><strong>{item.kayote}</strong></td>
                        <td>{item.total_drinks}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
                <tfoot style={{ backgroundColor: '#fff9c4', fontWeight: 'bold' }}>
                  <tr>
                    <td><strong>TOTAL</strong></td>
                    <td><strong>{reportData.summary.total_drinks}</strong></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
            {renderPagination(reportData.kayotee_breakdown.length)}
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderEntertainmentBills = (type) => {
    if (!reportData || !reportData.bills) return null;

    const isBar = type === 'bar';
    const icon = isBar ? '🍸' : '🍕';
    const title = isBar ? 'Bar Entertainment Bills' : 'Food Entertainment Bills';

    return (
      <div className="th-report-results">
        <Card className={`summary-card-header ${isBar ? 'bar-ent-header' : 'food-ent-header'} mb-4`}>
          <Card.Body>
            <Row>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">{icon}</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Bills</div>
                    <div className="th-stat-value">{reportData.summary.total_bills}</div>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">📦</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Quantity</div>
                    <div className="th-stat-value">{reportData.summary.total_quantity}</div>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">💰</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Total Amount</div>
                    <div className="th-stat-value">{formatCurrency(reportData.summary.total_amount)}</div>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="th-summary-stat">
                  <div className="th-stat-icon">🪑</div>
                  <div className="th-stat-content">
                    <div className="th-stat-label">Unique Tables</div>
                    <div className="th-stat-value">{reportData.summary.unique_tables}</div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="th-data-table-card">
          <Card.Header className="th-entertainment-header">
            <h5>{icon} {title}</h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Item Name</th>
                    {isBar && <th>Category</th>}
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const indexOfLastItem = currentPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = reportData.bills.slice(indexOfFirstItem, indexOfLastItem);
                    return currentItems.map((bill, idx) => (
                      <tr key={idx}>
                        <td>{new Date(bill.setup_dte).toLocaleDateString()}</td>
                        <td>{bill.item_name}</td>
                        {isBar && <td>{bill.category}</td>}
                        <td>{bill.quantity}</td>
                        <td>{formatCurrency(bill.rate)}</td>
                        <td className="th-amount-cell">{formatCurrency(bill.amount)}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
                <tfoot style={{ backgroundColor: '#fff9c4', fontWeight: 'bold' }}>
                  <tr>
                    <td colSpan={isBar ? "3" : "2"}><strong>TOTAL</strong></td>
                    <td><strong>{reportData.bills.reduce((sum, bill) => sum + (Number(bill.quantity) || 0), 0)}</strong></td>
                    <td></td>
                    <td className="th-amount-cell"><strong>{formatCurrency(reportData.summary.total_amount)}</strong></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
            {renderPagination(reportData.bills.length)}
          </Card.Body>
        </Card>
      </div>
    );
  };

  return (
    <Layout>
      <div className="table-history-page" style={{ padding: '8px' }}>
        {/* Page Header */}
        <div style={{ 
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '12px' 
        }}>
          <Title level={4} style={{ margin: 0, color: '#1565c0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TableOutlined />
            Table History & Sales Reports
          </Title>
          <Text style={{ color: '#1976d2' }}>Comprehensive sales analysis and reporting</Text>
        </div>

        {/* Filters Section */}
        <Card 
          title={
            <Space>
              <SearchOutlined style={{ color: '#3f51b5' }} />
              <span style={{ color: '#3f51b5' }}>Filter Options</span>
            </Space>
          }
          style={{ marginBottom: '12px' }}
          headStyle={{ background: 'linear-gradient(90deg, #e8eaf6 0%, #c5cae9 100%)' }}
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Text strong style={{ fontSize: '12px', color: '#999' }}>Table No</Text>
                <Input
                  size="small"
                  placeholder="Enter table number"
                  value={formData.tableNo}
                  onChange={(e) => handleInputChange('tableNo', e.target.value)}
                />
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Text strong style={{ fontSize: '12px', color: '#999' }}>Waiter No</Text>
                <Input
                  size="small"
                  placeholder="Enter waiter number"
                  value={formData.waiterNo}
                  onChange={(e) => handleInputChange('waiterNo', e.target.value)}
                />
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Text strong style={{ fontSize: '12px', color: '#999' }}>Start Date</Text>
                <DatePicker
                  size="small"
                  value={formData.startDate}
                  onChange={(date) => handleInputChange('startDate', date)}
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                />
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Text strong style={{ fontSize: '12px', color: '#999' }}>End Date</Text>
                <DatePicker
                  size="small"
                  value={formData.endDate}
                  onChange={(date) => handleInputChange('endDate', date)}
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                />
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Text strong style={{ fontSize: '12px', color: '#999' }}>Zone</Text>
                <Select
                  size="small"
                  value={formData.zone}
                  onChange={(value) => handleInputChange('zone', value)}
                  style={{ width: '100%' }}
                >
                  <Option value="Club">Club</Option>
                  <Option value="Pool">Pool</Option>
                  <Option value="Skybar">Skybar</Option>
                </Select>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Action Buttons Section */}
        <Card title={<Space><TableOutlined /> Report Actions</Space>} style={{ marginBottom: '12px' }}>
          <Space wrap size="small">
            <Button 
              type="primary" 
              size="small"
              icon={<SearchOutlined />}
              onClick={handleSearchByWaiter}
              loading={loading}
            >
              Search by Waiter
            </Button>
            <Button 
              size="small"
              icon={<TableOutlined />}
              onClick={handleTableSummary}
              loading={loading}
            >
              Table Summary
            </Button>
            <Button 
              size="small"
              icon={<StarOutlined />}
              onClick={handleVIPTableSummary}
              loading={loading}
              style={{ borderColor: '#faad14', color: '#faad14' }}
            >
              VIP Tables
            </Button>
            <Button 
              type="primary"
              size="small"
              onClick={handleShishaAnalysis}
              loading={loading}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              Shisha Analysis
            </Button>
            <Button 
              size="small"
              icon={<CalendarOutlined />}
              onClick={handleShishaByDate}
              loading={loading}
            >
              Shisha by Date
            </Button>
            <Button 
              size="small"
              onClick={handleBarEntertainment}
              loading={loading}
            >
              Bar Entertainment
            </Button>
            <Button 
              size="small"
              onClick={handleFoodEntertainment}
              loading={loading}
            >
              Food Entertainment
            </Button>
            <Button 
              size="small"
              onClick={handleKayoteeAnalysis}
              loading={loading}
              style={{ borderColor: '#faad14', color: '#faad14' }}
            >
              Kayotee Analysis
            </Button>
          </Space>
        </Card>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" tip="Loading report data..." />
          </div>
        )}

        {/* Report Results */}
        {!loading && reportData && (
          <>
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button 
                danger
                size="small"
                icon={<FilePdfOutlined />}
                onClick={exportToPDF}
              >
                Export to PDF
              </Button>
              <Button 
                type="primary"
                size="small"
                icon={<FileExcelOutlined />}
                onClick={exportToExcel}
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
              >
                Export to Excel
              </Button>
            </div>
            {activeReport === 'Waiter Analysis' && renderWaiterAnalysis()}
            {activeReport === 'Table Summary' && renderTableSummary()}
            {activeReport === 'VIP Table Summary' && renderVIPTableSummary()}
            {activeReport === 'Shisha Analysis' && renderShishaAnalysis()}
            {activeReport === 'Shisha Analysis by Date' && renderShishaByDate()}
            {activeReport === 'Bar Entertainment Bills' && renderEntertainmentBills('bar')}
            {activeReport === 'Food Entertainment Bills' && renderEntertainmentBills('food')}
            {activeReport === 'Kayotee Analysis' && renderKayoteeAnalysis()}
          </>
        )}

        {/* Empty State */}
        {!loading && !reportData && (
          <Alert
            message="No Report Selected"
            description="Select a report type above to view sales data and analytics"
            type="info"
            showIcon
            icon={<SearchOutlined style={{ fontSize: 50 }} />}
          />
        )}
      </div>
    </Layout>
  );
};

export default TableHistory;
